import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
    Box,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {useIntl, FormattedMessage} from 'react-intl'
import Search from '../../components/search'
import ProductRegistrationImageContainer from './partials/product-registration-image-container'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import routingObject from '../../utils/static-routes'
import ProductRegistrationHeader from './partials/product-registration-header'
import {Helmet} from 'react-helmet'
import {
    isSerialNumberRegistrationCategory,
    useProductRegistrationData,
} from '../../custom-hooks/use-product-registration-data'
import ProductRegistrationBenefitCard from './partials/product-registration-benefit-card'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {CloseIcon} from '../../components/icons'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useTokenHelper} from '../../utils/token-helper'
import {fetchSerialNumberDuplication, PRODUCT_REGISTRATION_DUPLICATION_KEY} from '../../utils/utils'
import ProductRegistrationModal from './partials/product-registration-modal'
import LoadingSpinner from '../../components/loading-spinner'

const ProductSearch = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})
    const [searchResults, setSearchResults] = useState([])
    const [suggestionRegistration, setSuggestionRegistration] = useState([])
    const [productSelect, setProductSelect] = useState('')
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
    const [isCheckingDuplication, setIsCheckingDuplication] = useState(false)
    const duplicationChecksRef = useRef(0)
    const duplicationPendingRef = useRef(false)
    const duplicationCheckRef = useRef(null)
    const appOrigin = useOrigin(false)
    const {fetchSAPCPIValidToken} = useTokenHelper()
    const navigate = useNavigation()
    const intl = useIntl()
    const {isOpen: isScannerOpen, onOpen: onScannerOpen, onClose: onScannerClose} = useDisclosure()
    //ADD onScannerOpen sull'onClick del button in mobile

    // Scanner state
    const videoRef = useRef(null)
    const scannerRef = useRef(null)
    const [scanError, setScanError] = useState('')

    const navigateToScannedSearch = useCallback(
        async (scanValue, scanType) => {
            const navigateToSearch = async (searchText, serialNumber = null) => {
                if ((await duplicationCheckRef.current(serialNumber || searchText)) === false) {
                    return
                }
                const searchParams = new URLSearchParams({text: searchText})
                if (serialNumber) searchParams.set('sn', serialNumber)
                navigate(
                    `${routingObject.PRODUCT_REGISTRATION_PATH}/select?${searchParams.toString()}`,
                )
            }

            if (
                scanType === 'data_matrix_19' ||
                scanType === 'barcode_19' ||
                scanType === 'barcode_18'
            ) {
                localStorage.setItem('prepopulatedSerialNumber', scanValue)
                const prefix = scanValue.substring(0, 6)
                const searchText =
                    scanType === 'data_matrix_19' || scanType === 'barcode_19'
                        ? `*${prefix}`
                        : prefix
                navigateToSearch(searchText, scanValue)
                return
            }

            if (scanType === 'ean') {
                localStorage.removeItem('prepopulatedSerialNumber')
                navigateToSearch(scanValue)
                return
            }

            if (scanType !== 'qr_url') return

            localStorage.removeItem('prepopulatedSerialNumber')

            try {
                const url = new URL(scanValue)

                if (url.pathname.includes(routingObject.PRODUCT_REGISTRATION_PATH)) {
                    const serialNumber =
                        url.searchParams.get('sn') || url.searchParams.get('serialNumber')
                    if (
                        serialNumber &&
                        (await duplicationCheckRef.current(serialNumber)) === false
                    ) {
                        return
                    }
                    navigate(url.pathname + url.search)
                    return
                }

                const rawSerialMatch = scanValue.match(/(?:\?|&)sn=([A-Za-z0-9]{18,19})/i)
                const serialNumber =
                    url.searchParams.get('sn') ||
                    url.searchParams.get('serialNumber') ||
                    rawSerialMatch?.[1] ||
                    ''

                if (/^[A-Za-z0-9]{19}$/.test(serialNumber)) {
                    localStorage.setItem('prepopulatedSerialNumber', serialNumber)
                    navigateToSearch(`*${serialNumber.substring(0, 6)}`, serialNumber)
                    return
                }

                if (/^[A-Za-z0-9]{18}$/.test(serialNumber)) {
                    localStorage.setItem('prepopulatedSerialNumber', serialNumber)
                    navigateToSearch(serialNumber.substring(0, 6), serialNumber)
                    return
                }

                const productCode =
                    url.searchParams.get('pid') ||
                    url.pathname.match(/\/p\/([^/?#]+)/)?.[1] ||
                    scanValue.match(/\/p\/([^/?#]+)/)?.[1]

                if (productCode) {
                    navigateToSearch(productCode)
                }
            } catch {
                console.error('Invalid QR URL:', scanValue)
            }
        },
        [navigate],
    )

    const handleScanResult = useCallback(
        (result) => {
            const {rawValue, matchedType} = result
            const matchedTypeId =
                typeof matchedType === 'string' ? matchedType : matchedType?.type || null
            if (!rawValue) return

            // Stop scanner after successful scan
            if (scannerRef.current) {
                scannerRef.current.stop()
                scannerRef.current = null
            }
            onScannerClose()

            navigateToScannedSearch(rawValue, matchedTypeId)
        },
        [navigateToScannedSearch, onScannerClose],
    )

    const startScanner = useCallback(async () => {
        setScanError('')
        if (typeof window === 'undefined') return

        try {
            const {SerialBarcodeScanner} = await import('@taggun/serial-barcode-sdk')
            const scanner = new SerialBarcodeScanner({
                video: videoRef.current,
                formats: ['qr_code', 'data_matrix', 'code_128', 'ean_13', 'ean_8'],
                types: [
                    {
                        type: 'ean',
                        formats: ['ean_13', 'ean_8'],
                        regex: '^(?:\\d{8}|\\d{13})$',
                    },
                    {
                        type: 'data_matrix_19',
                        formats: ['data_matrix'],
                        regex: '^.{19}$',
                    },
                    {
                        type: 'barcode_19',
                        formats: ['code_128'],
                        regex: '^.{19}$',
                    },
                    {
                        type: 'barcode_18',
                        formats: ['code_128'],
                        regex: '^.{18}$',
                    },
                    {
                        type: 'qr_url',
                        formats: ['qr_code'],
                        regex: '^https?://',
                        flags: 'i',
                    },
                ],
                onResult: handleScanResult,
                onError: (error) => {
                    console.error('Scanner error:', error.code, error.message)
                    setScanError(
                        intl.formatMessage({
                            id: 'product_registration.scanner_error',
                            defaultMessage:
                                'Could not start scanner. Please check camera permissions.',
                        }),
                    )
                },
            })
            scannerRef.current = scanner
            await scanner.start()
        } catch (err) {
            console.error('Scanner init error:', err)
            setScanError(
                intl.formatMessage({
                    id: 'product_registration.scanner_error',
                    defaultMessage: 'Could not start scanner. Please check camera permissions.',
                }),
            )
        }
    }, [handleScanResult, intl])

    // Start scanner when modal opens, stop when it closes
    useEffect(() => {
        if (isScannerOpen) {
            // Small delay to ensure the video element is mounted
            const timer = setTimeout(() => startScanner(), 100)
            return () => clearTimeout(timer)
        } else if (scannerRef.current) {
            scannerRef.current.stop()
            scannerRef.current = null
        }
    }, [isScannerOpen, startScanner])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (scannerRef.current) {
                scannerRef.current.stop()
                scannerRef.current = null
            }
        }
    }, [])

    // Determine variant from URL: if ?category= param exists → SN variant, otherwise → SKU variant
    const queryParams =
        typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams()
    const {
        categories,
        skuImage,
        modelNumberImage,
        modelNumberImageLegacy,
        serialNumberTitle1,
        serialNumberDescription1,
        serialNumberTitle2,
        serialNumberDescription2,
        modelNumberTitle1,
        modelNumberDescription1,
        modelNumberTitle2,
        modelNumberDescription2,
        webPageSchemaMarkup,
    } = useProductRegistrationData()

    const categoryParam = queryParams.get('category')
    const selectedCategory = categories.find(
        (category) => category.label.toLowerCase() === categoryParam?.trim().toLowerCase(),
    )
    const categoryPath =
        categoryParam?.trim().toLowerCase() === 'legacy'
            ? 'Legacy'
            : selectedCategory?.categoryPath ||
              (typeof window !== 'undefined'
                  ? localStorage.getItem('productRegistrationSearchCategory')
                  : null)
    const isSNVariant = isSerialNumberRegistrationCategory(categoryPath)

    useEffect(() => {
        sessionStorage.removeItem(PRODUCT_REGISTRATION_DUPLICATION_KEY)
    }, [])

    const handleRegistrationInputSubmit = async (input) => {
        if (duplicationPendingRef.current || isDuplicateModalOpen) return false
        if (!isSNVariant || duplicationChecksRef.current >= 2) return true
        const serialNumber = input.trim()
        if (!/^(?:[A-Za-z0-9]{18,19}|\d{13})$/.test(serialNumber)) return true

        const isFirstCheck = duplicationChecksRef.current === 0
        duplicationChecksRef.current += 1
        duplicationPendingRef.current = true
        setIsCheckingDuplication(true)
        try {
            sessionStorage.removeItem(PRODUCT_REGISTRATION_DUPLICATION_KEY)
            const accessToken = await fetchSAPCPIValidToken()
            const duplication = await fetchSerialNumberDuplication(
                appOrigin,
                accessToken,
                serialNumber,
            )
            sessionStorage.setItem(
                PRODUCT_REGISTRATION_DUPLICATION_KEY,
                JSON.stringify({serialNumber, duplicationToken: duplication.duplicationToken}),
            )
            if (isFirstCheck && duplication.hasDuplicates) {
                localStorage.removeItem('prepopulatedSerialNumber')
                setIsDuplicateModalOpen(true)
                return false
            }
            return true
        } catch (error) {
            console.error('Error during serial number duplication check:', error)
            return true
        } finally {
            duplicationPendingRef.current = false
            setIsCheckingDuplication(false)
        }
    }
    duplicationCheckRef.current = handleRegistrationInputSubmit

    useEffect(() => {
        if (typeof window !== 'undefined' && categoryPath) {
            localStorage.setItem('productRegistrationSearchCategory', categoryPath)
        }
    }, [categoryPath])

    const snPlaceholder = intl.formatMessage({
        id: 'product_registration.search_sn',
        defaultMessage: 'Insert your serial number here',
    })

    const skuPlaceholder = intl.formatMessage({
        id: 'product_registration.search_sku',
        defaultMessage: 'Insert your product code here',
    })

    const placeholder = isSNVariant ? snPlaceholder : skuPlaceholder

    useEffect(() => {
        if (
            (searchResults &&
                productSelect.length !== 0 &&
                searchResults?.hits !== undefined &&
                searchResults?.length !== 0) ||
            (suggestionRegistration?.productId && searchResults && productSelect)
        ) {
            const storedSN = isSNVariant ? localStorage.getItem('prepopulatedSerialNumber') : null
            const snParam = storedSN ? `&sn=${encodeURIComponent(storedSN)}` : ''
            navigate(
                `${routingObject.PRODUCT_REGISTRATION_PATH}/select?text=` +
                    productSelect.replaceAll(' ', '-') +
                    snParam,
            )
        }
    }, [searchResults, productSelect, suggestionRegistration, isSNVariant])

    const {cardsData} = useProductRegistrationData()

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            {isCheckingDuplication && <LoadingSpinner />}
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader index={1} variant={'product'} />
            </Flex>

            <Flex
                direction={['column', 'column', 'column', 'row']}
                gap={['40px', '40px', '40px', '160px']}
                alignSelf="stretch"
                alignItems="flex-start"
            >
                {/* Left section: Search + Category cards */}
                <Flex direction="column" gap="40px" flex="1" alignSelf="stretch">
                    {/* Search section */}
                    <HideOnDesktop>
                        <Flex {...styles.bottomContainer} w="100%">
                            <Flex {...styles.labelSearchOuterContainer} w="100%">
                                <Flex {...styles.labelSearchInnerContainer} w="100%" flex="1">
                                    <Text {...styles.labelSearchTitle}>
                                        {isSNVariant ? (
                                            <FormattedMessage
                                                id="product_registration.scan_or_enter_sn"
                                                defaultMessage="Scan your product serial number"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="product_registration.enter_sku_or_model"
                                                defaultMessage="Enter the SKU or Model Number of your product"
                                            />
                                        )}
                                    </Text>
                                    {isSNVariant && (
                                        <>
                                            <Button
                                                variant="primary"
                                                onClick={onScannerOpen}
                                                w="100%"
                                                my="8px"
                                            >
                                                <FormattedMessage
                                                    id="product_registration.open_camera_to_scan"
                                                    defaultMessage="Open camera to scan"
                                                />
                                            </Button>
                                            <Text
                                                textStyle={'body-small'}
                                                color="text.default"
                                                fontWeight={'regular'}
                                            >
                                                <FormattedMessage
                                                    id="product_registration.or_type_serial_number"
                                                    defaultMessage="or type the Serial Number below"
                                                />
                                            </Text>
                                        </>
                                    )}
                                    <Box {...styles.searchBarOuterContainer} w="100%">
                                        <Flex
                                            gap="12px"
                                            alignItems="center"
                                            alignSelf="stretch"
                                            w="100%"
                                        >
                                            <Box flex="1">
                                                <Search
                                                    ariaLabel={placeholder}
                                                    searchInputPlaceholder={placeholder}
                                                    variant={'productSearch'}
                                                    setSearchResults={setSearchResults}
                                                    isProductRegistration
                                                    isProductRegistrationSN={isSNVariant}
                                                    onRegistrationInputSubmit={
                                                        handleRegistrationInputSubmit
                                                    }
                                                    setProductSelect={setProductSelect}
                                                    setSuggestionRegistration={
                                                        setSuggestionRegistration
                                                    }
                                                    shouldRemoveLabel
                                                    showSpellingMessage={
                                                        searchResults?.hits === undefined &&
                                                        searchResults?.length !== 0 &&
                                                        !suggestionRegistration?.productId
                                                    }
                                                />
                                            </Box>
                                        </Flex>
                                        {!isSNVariant && (
                                            <Flex flexDir={'column'} gap="8px">
                                                <Text
                                                    textStyle={'body-xsmall'}
                                                    fontWeight={'light'}
                                                >
                                                    <FormattedMessage
                                                        id="product_registration.sku_ex"
                                                        defaultMessage="Valid SKU examples: <i>0W22234994007, 0W22234994007, 0W22234994007</i>"
                                                        values={{i: (chunks) => <i>{chunks}</i>}}
                                                    />
                                                </Text>
                                                <Text
                                                    textStyle={'body-xsmall'}
                                                    fontWeight={'light'}
                                                >
                                                    <FormattedMessage
                                                        id="product_registration.model_number_examples"
                                                        defaultMessage="Valid Model Number examples: <i>EXAM.000.999.009, EXAM.000.999.009</i>"
                                                        values={{i: (chunks) => <i>{chunks}</i>}}
                                                    />
                                                </Text>
                                            </Flex>
                                        )}
                                    </Box>
                                </Flex>
                            </Flex>
                        </Flex>
                    </HideOnDesktop>
                    <HideOnMobile>
                        <Flex {...styles.bottomContainer} w="100%">
                            <Flex {...styles.labelSearchOuterContainer} w="100%">
                                <Flex {...styles.labelSearchInnerContainer} w="100%" flex="1">
                                    <Text {...styles.labelSearchTitle}>
                                        {isSNVariant ? (
                                            <FormattedMessage
                                                id="product_registration.enter_sn"
                                                defaultMessage="Type your product Serial Number"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="product_registration.enter_sku_or_model"
                                                defaultMessage="Enter the SKU or Model Number of your product"
                                            />
                                        )}
                                    </Text>
                                    <Box {...styles.searchBarOuterContainer} w="100%">
                                        <Flex
                                            gap="12px"
                                            alignItems="center"
                                            alignSelf="stretch"
                                            w="100%"
                                        >
                                            <Box flex="1">
                                                <Search
                                                    ariaLabel={placeholder}
                                                    searchInputPlaceholder={placeholder}
                                                    variant={'productSearch'}
                                                    setSearchResults={setSearchResults}
                                                    isProductRegistration
                                                    isProductRegistrationSN={isSNVariant}
                                                    onRegistrationInputSubmit={
                                                        handleRegistrationInputSubmit
                                                    }
                                                    setProductSelect={setProductSelect}
                                                    setSuggestionRegistration={
                                                        setSuggestionRegistration
                                                    }
                                                    shouldRemoveLabel
                                                    showSpellingMessage={
                                                        searchResults?.hits === undefined &&
                                                        searchResults?.length !== 0 &&
                                                        !suggestionRegistration?.productId
                                                    }
                                                />
                                            </Box>
                                        </Flex>
                                        {!isSNVariant && (
                                            <Flex flexDir={'column'} gap="8px">
                                                <Text
                                                    textStyle={'body-xsmall'}
                                                    fontWeight={'light'}
                                                >
                                                    <FormattedMessage
                                                        id="product_registration.sku_ex"
                                                        defaultMessage="Valid SKU examples: <i>0W22234994007, 0W22234994007, 0W22234994007</i>"
                                                        values={{i: (chunks) => <i>{chunks}</i>}}
                                                    />
                                                </Text>
                                                <Text
                                                    textStyle={'body-xsmall'}
                                                    fontWeight={'light'}
                                                >
                                                    <FormattedMessage
                                                        id="product_registration.model_number_examples"
                                                        defaultMessage="Valid Model Number examples: <i>EXAM.000.999.009, EXAM.000.999.009</i>"
                                                        values={{i: (chunks) => <i>{chunks}</i>}}
                                                    />
                                                </Text>
                                            </Flex>
                                        )}
                                    </Box>
                                </Flex>
                            </Flex>
                        </Flex>
                    </HideOnMobile>
                    {/* Category section title */}
                    <Text textStyle="head-small" fontWeight="medium" color="text.default">
                        {isSNVariant ? (
                            <FormattedMessage
                                id="product_registration.serial_number_location"
                                defaultMessage="Where is the serial number located?"
                            />
                        ) : (
                            <FormattedMessage
                                id="product_registration.model_number_location"
                                defaultMessage="Where are the SKU and Model Number located?"
                            />
                        )}
                    </Text>

                    {/* Info banners on top */}
                    <Flex direction="column" gap="16px" alignSelf="stretch" width={'100%'}>
                        {isSNVariant ? (
                            <>
                                <ProductRegistrationImageContainer
                                    image={skuImage}
                                    title={serialNumberTitle1}
                                    description={serialNumberDescription1}
                                    variant="productSearch"
                                />
                                <ProductRegistrationImageContainer
                                    image={modelNumberImage}
                                    title={serialNumberTitle2}
                                    description={serialNumberDescription2}
                                    variant="productSearch"
                                />
                            </>
                        ) : (
                            <>
                                <ProductRegistrationImageContainer
                                    image={skuImage}
                                    title={modelNumberTitle1}
                                    description={modelNumberDescription1}
                                    variant="productSearch"
                                />
                                <ProductRegistrationImageContainer
                                    image={modelNumberImageLegacy}
                                    title={modelNumberTitle2}
                                    description={modelNumberDescription2}
                                    variant="productSearch"
                                />
                            </>
                        )}
                    </Flex>
                </Flex>

                {/* Right section: Benefits */}
                <Flex
                    direction="column"
                    gap={['16px', '16px', '16px', '24px']}
                    flex="1"
                    alignSelf="stretch"
                >
                    <Text textStyle="body-large" fontWeight="regular" color="text.default">
                        <FormattedMessage
                            id="product_registration.why_register"
                            defaultMessage="Why register your product?"
                        />
                    </Text>
                    <Flex direction="column" alignItems={'flex-start'} alignSelf="stretch">
                        {cardsData.map((card, index) => (
                            <ProductRegistrationBenefitCard
                                key={index}
                                icon={card.icon}
                                title={card.title}
                                description={card.description}
                                datatestid={card.testId}
                            />
                        ))}
                    </Flex>
                </Flex>
            </Flex>

            <ProductRegistrationModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                showActions={false}
                isSerialNumberDuplicate={false}
                requiresSerialNumberReinput={true}
            />
            {/* SN Scanner Modal */}
            <Modal isOpen={isScannerOpen} onClose={onScannerClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader
                        borderBottom={'1px solid'}
                        borderColor={'border.default'}
                        padding={'0 0 16px 0'}
                        marginTop={'40px'}
                        marginX={'24px'}
                        marginBottom={'16px'}
                        display={'flex'}
                        flexDir={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Text
                            textStyle={'body-large'}
                            color={'text.default'}
                            fontWeight={'regular'}
                        >
                            <FormattedMessage
                                id="product_registration.scan_qr_code"
                                defaultMessage="Scan QR code or Data Matrix"
                            />
                        </Text>
                        <Box
                            as="button"
                            display="flex"
                            tabIndex={0}
                            onClick={onScannerClose}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    onScannerClose()
                                }
                            }}
                            data-testid="close-modal"
                        >
                            <CloseIcon role="button" />
                        </Box>
                    </ModalHeader>
                    <ModalBody pb="24px">
                        <Flex
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            gap="16px"
                        >
                            {scanError ? (
                                <Text color="red.500" textAlign="center">
                                    {scanError}
                                </Text>
                            ) : (
                                <>
                                    <Box
                                        w="100%"
                                        maxW="500px"
                                        borderRadius="8px"
                                        overflow="hidden"
                                        bg="black"
                                    >
                                        <video
                                            ref={videoRef}
                                            id="scanner-preview"
                                            autoPlay
                                            playsInline
                                            muted
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                minHeight: '300px',
                                            }}
                                        />
                                    </Box>
                                    <Box
                                        paddingX="26px"
                                        backgroundColor={'background.default'}
                                        position="absolute"
                                        bottom="2.3125rem"
                                    >
                                        <Text
                                            textStyle="body-small"
                                            color="text.default"
                                            textAlign="center"
                                        >
                                            <FormattedMessage
                                                id="product_registration.scanner_hint"
                                                defaultMessage="Center the code that you need to scan"
                                            />
                                        </Text>
                                    </Box>
                                </>
                            )}
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    )
}

export default ProductSearch
